import { useEffect, useState, FC } from "react";

import { Alert, Button, Snackbar, Stack, TextField } from "@mui/material";
import "./dev.scss";
import {
  PartnerOrgModel,
  RubiOrgModel,
  getPartnerOrg,
  getPartnerOrgRubiOrg,
  patchPartnerOrg,
} from "@rubi/api-generated";
import { Link, useLocation, useParams } from "react-router-dom";

import { Breadcrumbs, Crumb, DevPage, ErrorMsg, Section } from "./common";
import { PartnerCustomFiltersEditor } from "./PartnerCustomFiltersEditor";

export const DevPartnerOrgPage: FC = () => {
  const location = useLocation();
  const { id } = useParams();
  const [partnerOrg, setPartnerOrg] = useState<PartnerOrgModel | null>(null);
  const [loadErrorMsg, setLoadErrorMsg] = useState<string | null>(null);
  const [hostingOrg, setHostingOrg] = useState<RubiOrgModel | null>(null);
  const [hostingOrgLoaded, setHostingOrgLoaded] = useState(false);
  const [hostingOrgError, setHostingOrgError] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const loadHostingOrg = async (partnerOrgId: string) => {
    setHostingOrgLoaded(false);
    setHostingOrgError(null);
    try {
      const response = await getPartnerOrgRubiOrg({
        path: { partner_org_id: partnerOrgId },
        throwOnError: false,
      });
      if (response.status === 200) {
        setHostingOrg(response.data ?? null);
      } else {
        setHostingOrgError(
          "" + response.status + " " + response.error?.console_msg,
        );
      }
    } catch (e) {
      setHostingOrgError(
        e instanceof Error ? e.message : "Failed to load hosting org",
      );
    } finally {
      setHostingOrgLoaded(true);
    }
  };

  const loadPartnerOrg = async (partnerOrgId: string) => {
    const response = await getPartnerOrg({
      path: { partner_org_id: partnerOrgId },
      throwOnError: false,
    });
    if (response.status === 200) {
      setPartnerOrg(response.data!);
      setTitle(response.data!.title);
    } else {
      setLoadErrorMsg("" + response.status + " " + response.error?.console_msg);
    }
  };

  useEffect(() => {
    if (!id) return;
    const state = location.state as
      | { partnerOrg?: PartnerOrgModel; hostingOrg?: RubiOrgModel }
      | undefined;
    if (state?.partnerOrg && state.partnerOrg.id === id) {
      setPartnerOrg(state.partnerOrg);
      setTitle(state.partnerOrg.title);
    } else {
      loadPartnerOrg(id);
    }
    if (state?.hostingOrg && state.hostingOrg.partner_org_id === id) {
      setHostingOrg(state.hostingOrg);
      setHostingOrgLoaded(true);
    } else {
      loadHostingOrg(id);
    }
  }, [id]);

  const isDirty = partnerOrg != null && title !== partnerOrg.title;
  const canSave = isDirty && !saving && title.trim() !== "";

  const handleSave = async () => {
    if (!id || !partnerOrg) return;
    setSaving(true);
    setSaveError(null);
    try {
      const response = await patchPartnerOrg({
        path: { partner_org_id: id },
        body: { title: title.trim() },
        throwOnError: false,
      });
      if (response.status === 200) {
        setPartnerOrg(response.data!);
        setTitle(response.data!.title);
        setSaveSuccess(true);
      } else {
        setSaveError("" + response.status + " " + response.error?.console_msg);
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DevPage
      title={partnerOrg?.title || "Unknown"}
      breadcrumbs={
        <Breadcrumbs>
          <Crumb>
            <Link to="/">Home</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin">Developer and Administrator Utilities</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin/partner_org">Partner Orgs</Link>
          </Crumb>
          <Crumb>{id}</Crumb>
        </Breadcrumbs>
      }
    >
      <Section subtitle="Details">
        <ErrorMsg msg={loadErrorMsg} />
        <table className="dev-table dev-table--details">
          <tbody>
            <tr>
              <td>id</td>
              <td className="mono">{partnerOrg?.id}</td>
            </tr>
            <tr>
              <td>time_created</td>
              <td className="mono muted">{partnerOrg?.time_created}</td>
            </tr>
            <tr>
              <td>time_updated</td>
              <td className="mono muted">{partnerOrg?.time_updated}</td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section subtitle="Hosting org">
        {!hostingOrgLoaded ? (
          <span className="muted">Loading…</span>
        ) : hostingOrgError ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <ErrorMsg msg={`Failed to load hosting org: ${hostingOrgError}`} />
            <Button size="small" onClick={() => id && loadHostingOrg(id)}>
              Retry
            </Button>
          </Stack>
        ) : hostingOrg ? (
          <>
            The staff login org for this partner is{" "}
            <Link
              to={`/dev_admin/org/${hostingOrg.id}`}
              state={{ org: hostingOrg }}
            >
              {hostingOrg.title}
            </Link>
            . Edit its approved apps / default permissions there.
          </>
        ) : (
          <span className="muted">
            No hosting org is linked to this partner yet.
          </span>
        )}
      </Section>

      <Section subtitle="Update">
        <Stack spacing={2} sx={{ maxWidth: 560 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={title.trim() === ""}
            helperText={
              title.trim() === "" ? "Title cannot be empty" : undefined
            }
          />
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!canSave}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </Stack>
        </Stack>
      </Section>

      <Section subtitle="Custom filters">
        {partnerOrg && id ? (
          <PartnerCustomFiltersEditor
            partnerOrgId={id}
            customFilters={partnerOrg.custom_filters}
            onSaved={(saved) => {
              setPartnerOrg(saved);
              setSaveSuccess(true);
            }}
          />
        ) : (
          <span className="muted">Loading…</span>
        )}
      </Section>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
      >
        <Alert severity="success" onClose={() => setSaveSuccess(false)}>
          Saved
        </Alert>
      </Snackbar>
    </DevPage>
  );
};
